import { useEffect, useRef } from 'react';
import { NotePencil, X } from '@phosphor-icons/react';
import { useIslandState, useIslandActions } from '../IslandProvider';
import { escapeSchliesstFormular } from '../form/form-logik';
import { BADGE_ICON, FORM, GLANZ, KOPF_FARBE } from '../form/form-stil';

/** Formular-Rahmen (Handoff Pillen-Formulare §2/§3): Kopfzeile mit App-Icon, Titel, Untertitel,
 *  „* Pflichtfeld“ und Schließen; Inhalt scrollt, die Knopfleiste des Formulars bleibt unten stehen.
 *  Umgestellte Formulare (openForm mit `meta`) bringen ihren Innenabstand über FormRahmen selbst mit;
 *  ältere Formulare ohne `meta` bekommen wie bisher 18 px Abstand. */
export function FormContent({ mobil }: { mobil: boolean }) {
  const { formTitle, formMeta, pageForm } = useIslandState();
  const { closeForm, setFormContentHeight } = useIslandActions();
  const kopfRef = useRef<HTMLDivElement>(null);
  const innenRef = useRef<HTMLDivElement>(null);
  const knopf = mobil ? 44 : 32;
  const farbe = KOPF_FARBE[formMeta?.farbe ?? 'gold'];
  const Icon = formMeta?.icon ?? NotePencil;
  const Badge = formMeta?.badge ? BADGE_ICON[formMeta.badge] : null;

  // Escape schließt – es sei denn, ein Baustein (aufgeklappte Liste) hat die Taste schon verbraucht.
  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (escapeSchliesstFormular(e)) closeForm(); };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [closeForm]);

  // Natürliche Höhe (Kopf + Trennlinie + Inhalt) messen → die Pille wächst damit bis 760 px.
  useEffect(() => {
    const kopf = kopfRef.current;
    const innen = innenRef.current;
    if (!kopf || !innen) return;
    const messen = () => setFormContentHeight(Math.ceil(kopf.offsetHeight + 0.5 + innen.offsetHeight));
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(kopf);
    beobachter.observe(innen);
    messen();
    return () => { beobachter.disconnect(); setFormContentHeight(null); };
    // pageForm: openForm kann den Inhalt austauschen, ohne dass FormContent neu eingehängt wird.
  }, [setFormContentHeight, pageForm]);

  return (
    // data-theme="dark": Tokens und ui-Bausteine lösen in der (immer dunklen) Pille dunkel auf, auch im hellen Modus.
    <div data-theme="dark" className="relative flex h-full flex-col" style={{ color: FORM.text }}>
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{
        background: `radial-gradient(90% 50% at 0% 0%,${farbe.toenung},transparent 70%),radial-gradient(70% 40% at 100% 100%,rgba(226,192,141,0.07),transparent 70%),${FORM.hintergrund}`,
      }} />
      <div ref={kopfRef} className="relative flex shrink-0 items-center" style={{ gap: 12, padding: '14px 14px 12px 16px' }}>
        <span aria-hidden className="pf-kopf-pop relative shrink-0" style={{ width: 44, height: 44 }}>
          <span className="absolute inset-0 overflow-hidden" style={{
            borderRadius: 12, background: farbe.verlauf,
            boxShadow: `inset 0 1px 1px rgba(255,255,255,0.55),inset 0 -3px 6px rgba(0,0,0,0.22),0 10px 22px -8px ${farbe.glow}`,
          }}>
            <span className="absolute inset-0" style={{ background: GLANZ }} />
            <span className="absolute rounded-full" style={{ width: 32, height: 32, right: -10, bottom: -12, background: 'rgba(255,255,255,0.16)' }} />
            <span className="absolute rounded-full" style={{ width: 16, height: 16, left: -5, top: -6, background: 'rgba(255,255,255,0.14)' }} />
            <span className="absolute inset-0 flex items-center justify-center">
              <Icon size={22} weight="fill" color="#fff" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.28))' }} />
            </span>
          </span>
          {Badge && (
            <span className="absolute flex items-center justify-center rounded-full" style={{
              right: -5, bottom: -5, width: 22, height: 22, background: 'linear-gradient(160deg,#ffffff,#e9e9ee)',
              boxShadow: '0 0 0 2.5px rgba(20,20,24,1),0 3px 8px rgba(0,0,0,0.45)',
            }}>
              <Badge size={11} weight="bold" color={farbe.badge} />
            </span>
          )}
        </span>
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
          <span id="pille-formular-titel" className="truncate" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.015em' }}>{formTitle}</span>
          {formMeta?.untertitel && (
            <span className="truncate" style={{ fontSize: 12, fontWeight: 600, color: FORM.text2 }}>{formMeta.untertitel}</span>
          )}
        </div>
        {formMeta?.pflichtHinweis && (
          <span data-tip="Mit * markierte Felder müssen ausgefüllt sein" className="shrink-0 whitespace-nowrap"
            style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.42)' }}>
            <span style={{ color: FORM.rot }}>*</span> Pflichtfeld
          </span>
        )}
        <button type="button" onClick={closeForm} aria-label="Schließen" data-tip="Schließen – Eingaben werden verworfen (Esc)"
          className="pf-schliessen flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none"
          style={{ width: knopf, height: knopf, background: 'rgba(255,255,255,0.1)', color: FORM.text }}>
          <X size={13} weight="bold" />
        </button>
      </div>
      <div aria-hidden className="relative shrink-0" style={{ height: 0.5, margin: '0 16px', background: FORM.linie }} />
      <div className="pf-scroll relative min-h-0 flex-1 overflow-y-auto">
        {/* Vollbild (Handy): Inhalt füllt die Höhe, damit die Knopfleiste unten steht. Am Desktop nicht –
            dort bestimmt die gemessene Inhaltshöhe die Pillenhöhe (min-height würde die Messung aufblähen). */}
        <div ref={innenRef} style={!formMeta ? { padding: 18 } : mobil ? { minHeight: '100%', display: 'flex', flexDirection: 'column' } : undefined}>
          {pageForm}
        </div>
      </div>
    </div>
  );
}
